/**
 * Display preferences follow the account (plan 9b, spec 2026-10-02 upacara-bersama-dan-akun
 * 5.4), as logic the tests run without a browser; ./preferensiAkun binds it to the API, the
 * session and the html attributes. Relative imports only; no DOM.
 *
 * - At a sign-in or an unlock, this person's copy applies at once (offline too), else what this
 *   device showed before preferences followed the account, else the device's own defaults:
 *   never another person's copy (a shared device, spec 15.4).
 * - Online, the server's answer decides: the person's own. An account without its own keeps
 *   what this device showed before the update (its old choice, or its own defaults), so no
 *   one's display changes at the update; only on a device with nothing from before do the
 *   parish defaults apply, else the device's. The result is stored as this person's copy, the
 *   one index.html applies next time. An answer for another account is never applied or
 *   stored; one that lands after the screen passed to someone else is stored for its owner, not
 *   shown, and never makes its owner the last person (the next PIN screen is not theirs).
 * - The older device choice is used once: the first person whose account has none when the
 *   device comes online takes it into the account (kept on the device if that fails). A
 *   device's own defaults are never written into an account.
 * - A change needs the server: it shows at once, is saved whole, and is put back if refused.
 *   An answer asked for before a change is older than the screen: it is dropped, never shown
 *   or stored over the change.
 */
import type { JawabanPreferensi, Preferensi } from '@satu/shared/preferensi'
import {
  type BawaanParoki,
  bacaSalinan,
  denganSalinan,
  pilihTampilan,
  type StorageLike,
  type Tampilan,
  tulisSalinan,
} from './tampilan'

export interface PreferensiDeps {
  storage: StorageLike | null
  /** GET /api/akun/preferensi, parsed; throws offline or when refused. */
  ambil(): Promise<JawabanPreferensi>
  /** PATCH /api/akun/preferensi with the whole preference, parsed. */
  simpan(preferensi: Preferensi): Promise<JawabanPreferensi>
  /** Shows a preference: the device store and the html attributes. */
  terapkan(pref: Tampilan): void
  /** The person the screen serves now ('' when nobody is active). */
  penggunaLayar(): string
  /** The device's own defaults (index.html's). */
  perangkat(): Tampilan
}

// The changes made on Akun on this device, counted: a load compares the count when it asks
// with the count when the answer lands.
let perubahan = 0

export function keKabel(t: Tampilan): Preferensi {
  return { kepadatan: t.density, ukuran_teks: t.text, gerakan: t.motion }
}

export function dariKabel(p: Preferensi): Tampilan {
  return { density: p.kepadatan, text: p.ukuran_teks, motion: p.gerakan }
}

function bawaanDari(b: JawabanPreferensi['bawaan_paroki']): BawaanParoki | null {
  return b === null ? null : { density: b.kepadatan, text: b.ukuran_teks }
}

export interface KeadaanSesi {
  status: string
  sesi: { userId: string }
}

/** A sign-in, an unlock, a start into the app, or another person while active. */
export function perluMuat(kini: KeadaanSesi, sebelum: KeadaanSesi): boolean {
  if (kini.status !== 'aktif' || kini.sesi.userId === '') return false
  return sebelum.status !== 'aktif' || sebelum.sesi.userId !== kini.sesi.userId
}

export async function muatPreferensi(userId: string, deps: PreferensiDeps): Promise<void> {
  const sejak = perubahan
  // A change made on Akun while the answer was on its way: the answer is older than the screen.
  const usang = () => perubahan !== sejak
  const awal = bacaSalinan(deps.storage)
  const salinan = Object.hasOwn(awal.orang, userId) ? awal.orang[userId] : undefined
  if (salinan !== undefined) {
    deps.terapkan(pilihTampilan(salinan, null, deps.perangkat()))
    tulisSalinan(deps.storage, { ...awal, terakhir: userId })
  } else {
    deps.terapkan(pilihTampilan(awal.sebelum, null, deps.perangkat()))
    // A device with nothing from before says so from now on, also when this load goes no
    // further: a later start never takes it for a device from before (catatTampilanSebelum).
    if (awal.sebelum === null) tulisSalinan(deps.storage, awal)
  }
  let jawab: JawabanPreferensi
  try {
    jawab = await deps.ambil()
  } catch {
    // Offline, or the session refused: the copy stands until the next sign-in or unlock.
    return
  }
  if (jawab.pengguna_id !== userId || usang()) return
  const kini = bacaSalinan(deps.storage)
  let pilihan: Tampilan
  if (jawab.preferensi !== null) {
    pilihan = dariKabel(jawab.preferensi)
  } else {
    pilihan =
      kini.sebelum !== null
        ? pilihTampilan(kini.sebelum, null, deps.perangkat())
        : pilihTampilan(null, bawaanDari(jawab.bawaan_paroki), deps.perangkat())
    if (kini.warisan !== null) {
      try {
        await deps.simpan(keKabel(pilihan))
      } catch {
        // Kept on the device: the next sign-in or unlock tries again.
        if (deps.penggunaLayar() === userId && !usang()) deps.terapkan(pilihan)
        return
      }
    }
  }
  if (usang()) return
  const diLayar = deps.penggunaLayar() === userId
  tulisSalinan(deps.storage, denganSalinan(bacaSalinan(deps.storage), userId, pilihan, diLayar))
  if (diLayar) deps.terapkan(pilihan)
}

export async function ubahTampilan(
  userId: string,
  sebelum: Tampilan,
  patch: Partial<Tampilan>,
  deps: PreferensiDeps,
): Promise<Tampilan> {
  const baru: Tampilan = { ...sebelum, ...patch }
  perubahan += 1
  deps.terapkan(baru)
  try {
    const jawab = await deps.simpan(keKabel(baru))
    if (jawab.pengguna_id !== userId) throw new Error('preferensi_akun_lain')
    const diLayar = deps.penggunaLayar() === userId
    tulisSalinan(deps.storage, denganSalinan(bacaSalinan(deps.storage), userId, baru, diLayar))
    return baru
  } catch (error) {
    if (deps.penggunaLayar() === userId) deps.terapkan(sebelum)
    throw error
  }
}
